<template>
  <component
    :is="modalTag"
    v-bind="modalAttrs">
    <dialog
      :class="[
        'mlui-core-modal modal in tw-flex modal-backdrop tw-justify-center coremodal coremodal-backdrop',
        'tw-flex tw-items-center tw-overflow-y-auto tw-overflow-x-hidden'
      ]">
      <div
        :class="modalClass"
        :data-test="dataTest"
        class="modal-dialog coremodal-content tw-flex-col tw-bg-core-primary-100"
        role="document">
        <div
          :class="[
            modalContentClass,
            {'modal__container': fullHeight}
          ]"
          class="modal-content tw-rounded-lg tw-max-h-full">
          <slot name="header">
            <div
              v-if="header"
              :class="header.class? header.class : 'tw-justify-between'"
              class="tw-p-5 tw-flex tw-justify-between tw-items-center">
              <FontIcon
                v-if="header.icon"
                :class="header.icon ? 'visible' : 'invisible'"
                :iconClass="header.icon || ''"
                class="tw-mr-2 tw-text-left tw-grow-0 tw-shrink-1 tw-basis-auto tw-text-core-primary-10" />
              <div
                v-if="header.title"
                :class="header.titleClass ? header.titleClass : ' tw-text-left'"
                class="tw-flex-auto headlines--headline-06 tw-text-core-primary-10"
                data-test="header-title">
                {{ header.title }}
              </div>
              <div
                v-if="header && header.onCloseHandler"
                class="modal-header-icon-close tw-cursor-pointer tw-grow-0 tw-shrink-1 tw-basis-auto"
                @click.stop="onCloseHandler">
                <SVGIcon
                  iconName="Close"
                  data-test="icon-close"
                  colorClass="tw-fill-core-neutral-cold-20" />
              </div>
            </div>
          </slot>
          <div
            :class="[
              bodyClass, {'modal-body-full-height': fullHeight},
              withoutScroll ? 'modal-body--not-scrollable' : 'modal-body--scrollable'
            ]"
            class="modal-body tw-flex tw-flex-col tw-bg-core-primary-100 tw-px-6 tw-flex-auto">
            <slot
              v-if="!loading"
              name="body" />
            <div
              v-if="loading"
              data-test="modal-loading"
              class="modal-loading">
              <Loading />
            </div>
          </div>
          <div
            :class="footerClass"
            class="modal-footer tw-gap-4 tw-p-5 tw-flex tw-justify-end tw-items-center tw-bg-core-primary-100 tw-rounded-b">
            <slot name="footer">
              <CoreButton
                v-if="dismissButton.visible"
                :class="dismissButton.class"
                :dataTest="dismissButton.dataTest"
                :disabled="dismissButton.disabled || fetching"
                buttonType="ghost"
                :label="dismissButton.label"
                @click="dismissButton.onClickHandler" />
              <CoreButton
                v-for="(button, index) in actionButtonsFiltered"
                v-bind="button"
                :key="index"
                :loading="fetching || button.loading"
                :buttonType="getButtonType(button.template, button.flat)"
                @click="button.onClickHandler" />
            </slot>
          </div>
        </div>
      </div>
    </dialog>
  </component>
</template>

<script>
import CoreButton from 'frontcore/components/CoreButton.vue'
import FontIcon from './FontIcon'
import Loading from './loadings/Loading'
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import { PORTAL_LEVELS } from 'frontcore/constants/constants'

export default {
  name: 'OldModal',
  components: {
    CoreButton,
    FontIcon,
    Loading,
    SVGIcon,
  },
  props: {
    actionButtons: {
      type: Array,
      default () {
        return []
      },
    },
    dismissButton: {
      type: Object,
      default () {
        return {
          visible: false,
        }
      },
    },
    portalTarget: {
      type: String,
      default: PORTAL_LEVELS.ONE,
    },
    fetching: {
      type: Boolean,
      default: false,
    },
    fullHeight: {
      type: Boolean,
      default: false,
    },
    header: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    modalClass: {
      type: [String, Object],
      required: false,
    },
    footerClass: {
      type: String,
      required: false,
    },
    modalContentClass: {
      type: String,
      required: false,
    },
    bodyClass: {
      type: String,
      required: false,
    },
    portal: {
      type: Boolean,
      default: false,
    },
    dataTest: {
      type: String,
    },
    withoutScroll: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    modalTag () {
      return this.portal ? 'portal' : 'div'
    },
    modalAttrs () {
      const attrs = { class: 'modal-native' }
      if (this.portal) {
        attrs.to = this.portalTarget
        attrs.tag = 'div'
      }
      return attrs
    },
    actionButtonsFiltered () {
      return this.actionButtons.filter(btn => btn.visible ?? true)
    },
  },
  unmounted () {
    this.enableScroll()
  },
  mounted () {
    document.body.classList.add('modal-open')
  },
  methods: {
    onCloseHandler () {
      if (this.header?.onCloseHandler && !this.loading) this.header.onCloseHandler()
    },
    enableScroll () {
      document.body.classList.remove('modal-open')
    },
    getButtonType (template, flat) {
      switch (template) {
      case 'neutral':
        return 'ghost'
      case 'danger':
        return 'destructive-ghost'
      default:
        if (flat) {
          return 'ghost'
        }
        return 'primary'
      }
    },
  },
}
</script>

<style lang="scss">

.mlui-core-modal {

  &.modal {
    overflow-y: hidden !important;
    display: block;
  }
  &.modal-backdrop {
    background-color: rgba(#000000, .5);
  }

  .modal-body {
    max-height: calc(100vh - 16rem);

    &.modal-body--scrollable {
      overflow: auto;
    }

    &.modal-body--not-scrollable {
      overflow: visible;
    }

    .modal-loaging {
      padding-top: 3rem;
    }

    &.modal-body-full-height {
      height: calc(100vh - 16rem);
    }
  }

  .modal__container {
    @include is-phone {
      height: 100%;
    }
  }

  .modal-dialog {
    @media (max-width: 415px) {
      width: 100%;
    }
    @media (min-width: 416px) and (max-width: 767px) {
      min-width: 95%;
    }
    @media (min-width: 768px) {
      min-width: 40rem;
    }
  }
  @media (min-width: 576px) {
    .modal-dialog {
      margin: 1.75rem auto;
      max-width: 500px;
    }
  }
  .mw-60 {
    width: 60%;
    max-width: 60%;
    @media (max-width: 1024px) {
      width: 90%;
      max-width: 90%;
      height: 90%;
    }
  }
  .mw-75 {
    max-width: 75%;
  }
  .mw-90 {
    max-width: 90% !important;
    width: 90% !important;
  }
}

</style>
